'use client'

import type { Space } from '@cocoonspace/sdk-js/types/space'
import { type ComponentProps, type MouseEventHandler, useCallback } from 'react'
import { getListSpacesQueryKey } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { Button } from '@cocoonspace/ui/components/button'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { HeartIcon, type LucideProps } from 'lucide-react'
import { usePathname } from 'next/navigation'

import { stripLocaleFromPath } from '~/i18n/routing'
import { useAuth } from '~/providers/auth-provider'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'
import { useSpaceFavoriteUpdate } from '../../hooks/use-space-favorite-update.hook'

export const SpaceFavoriteButton = ({
	space,
	iconProps,
	...props
}: {
	space: Pick<Space, 'id' | 'title' | 'favorite'>
	iconProps?: LucideProps
} & ComponentProps<typeof Button>) => {
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const queryClient = useQueryClient()
	const { t } = useLingui()
	const { track } = useTracking()
	const { isAuth, openRegister } = useAuth()
	const { userQuery } = useUserContext()

	const { mutateAsync } = useSpaceFavoriteUpdate()

	const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
		async (e) => {
			e.stopPropagation()
			e.preventDefault()

			if (isAuth) {
				await mutateAsync({
					favorite: userQuery.data?.favorites?.includes(space.id),
					id: space.id,
				})

				userQuery.refetch()
				queryClient.invalidateQueries({ queryKey: getListSpacesQueryKey() })
				track.updateFavorite({ route: pathname, space })
			} else {
				e.stopPropagation()
				openRegister()
			}
		},
		[
			isAuth,
			mutateAsync,
			space,
			userQuery,
			queryClient,
			track,
			pathname,
			openRegister,
		],
	)

	const isInFavorite = userQuery.data?.favorites?.includes(space.id)

	return (
		<Button
			variant='ghost'
			icon
			onClick={handleClick}
			size='sm'
			className='hover:bg-primary hover:text-primary-foreground group/favorite-button'
			{...props}
		>
			<HeartIcon
				{...iconProps}
				className={cn(
					'group-hover/favorite-button:fill-primary-foreground group-hover/favorite-button:stroke-primary-foreground',
					iconProps?.className,
					{ 'fill-primary stroke-primary': isInFavorite },
				)}
			/>

			<span className='sr-only'>{t`Ajouter à mes favoris`}</span>
		</Button>
	)
}
